/**
 * Ecran de connexion.
 *
 * Il evite de recreer un bonhomme a chaque partie: on se connecte avec un nom
 * et un mot de passe, et on repart exactement ou on s'etait arrete (position,
 * inventaire, besoins, maisons). IL FAUT UN COMPTE: le mode invite n'existe
 * plus ici — le serveur refuse de toute facon un jeton sans compte, et seuls
 * les tests de bout en bout jouent encore en invite, par la passerelle de
 * stockage local (voir `identifier` dans client/main.ts).
 *
 * Le mot de passe n'est verifie que par le serveur (sel + scrypt); rien de
 * sensible n'est conserve ici, seulement le jeton renvoye.
 */
import { bouton, el } from './commun.ts'
import { journal } from '../log/journal.ts'
import { estAdmin } from '../../shared/data/proprietaires.ts'
import { ecrireLocal, lireLocal } from '../stockage.ts'
import '../styles/connexion.css'

/**
 * Memoire locale du fait qu'on est administrateur.
 *
 * Elle ne sert QU'A afficher le bouton d'administration sans redemander au
 * serveur: le droit reel est reverifie a chaque requete `/api/admin/...` contre
 * la table des comptes. Trafiquer cette valeur dans le navigateur ne donne donc
 * aucun pouvoir.
 */
const CLE_ADMIN = 'jeu.admin'

export interface ResultatConnexion {
  /** Jeton du joueur. Toute la sauvegarde est indexee dessus. */
  token: string
  /** Nom du compte. L'ecran ne se referme que sur une connexion reussie. */
  compte: string
  /** Faux si le compte n'a pas encore de bonhomme: il faudra le creer. */
  aUnPersonnage: boolean
  /** Vrai si ce compte a les pouvoirs d'administration (dit par le serveur). */
  admin: boolean
}

interface ReponseConnexion {
  nom: string
  token: string
  aUnPersonnage: boolean
  admin?: boolean
}

/**
 * Vrai si le compte de cette session est administrateur, d'apres la derniere
 * connexion. A utiliser uniquement pour montrer ou cacher le bouton
 * d'administration.
 */
export function estAdminLocal(): boolean {
  return lireLocal(CLE_ADMIN) === '1'
}

export class Connexion {
  private parent: HTMLElement
  private racine: HTMLDivElement | null = null
  private resoudre: ((r: ResultatConnexion) => void) | null = null

  constructor(parent: HTMLElement) {
    this.parent = parent
  }

  /**
   * Affiche l'ecran et attend une connexion REUSSIE. Il n'y a pas d'autre
   * issue: le monde est partage, et l'on n'y entre pas sans compte.
   */
  ouvrir(nomPropose: string): Promise<ResultatConnexion> {
    journal.input('connexion', 'ecran de connexion affiche')
    return new Promise<ResultatConnexion>((resoudre) => {
      this.resoudre = resoudre
      this.construire(nomPropose)
    })
  }

  private construire(nomPropose: string): void {
    const racine = el('div', 'connexion')
    this.racine = racine

    const boite = el('div', 'connexion__boite')
    boite.append(el('div', 'connexion__titre', 'Jeu'))
    boite.append(el('div', 'connexion__sous-titre', 'Reprends ta partie là où tu l’as laissée.'))

    const champNom = document.createElement('input')
    champNom.type = 'text'
    champNom.id = 'connexion-nom'
    champNom.className = 'connexion__champ'
    champNom.placeholder = 'Nom'
    champNom.autocomplete = 'username'
    champNom.value = nomPropose
    champNom.setAttribute('aria-label', 'Nom')

    const champMdp = document.createElement('input')
    champMdp.type = 'password'
    champMdp.id = 'connexion-mdp'
    champMdp.className = 'connexion__champ'
    champMdp.placeholder = 'Mot de passe'
    champMdp.autocomplete = 'current-password'
    champMdp.setAttribute('aria-label', 'Mot de passe')

    const erreur = el('div', 'connexion__erreur')
    erreur.setAttribute('role', 'alert')

    // Indice discret: ce nom-la ouvrira le panneau d'administration. C'est un
    // simple rappel visuel, le droit vient du serveur.
    //
    // Sa classe lui appartient: il a longtemps porte `connexion__erreur`, ce qui
    // donnait DEUX elements de cette classe et rendait impossible de designer le
    // message d'erreur, au test comme au lecteur d'ecran.
    const indiceAdmin = el('div', 'connexion__indice')
    const majIndice = (): void => {
      indiceAdmin.textContent = estAdmin(champNom.value) ? '🛠️ Compte administrateur' : ''
    }
    champNom.addEventListener('input', majIndice)
    majIndice()

    const connecter = bouton('▶ Se connecter', () => void this.tenter(champNom, champMdp, erreur, connecter), 'bouton--vert connexion__valider')

    for (const champ of [champNom, champMdp]) {
      champ.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') void this.tenter(champNom, champMdp, erreur, connecter)
      })
    }

    boite.append(champNom, champMdp, indiceAdmin, erreur, connecter)
    racine.append(boite)
    this.parent.append(racine)
    // Focus SYNCHRONE, surtout pas dans un requestAnimationFrame: differe, il
    // arrivait apres que l'utilisateur avait deja saisi le champ suivant et lui
    // volait le focus — le mot de passe se retrouvait ecrit dans le champ du nom.
    champNom.focus()
  }

  private async tenter(
    champNom: HTMLInputElement,
    champMdp: HTMLInputElement,
    erreur: HTMLElement,
    valider: HTMLButtonElement,
  ): Promise<void> {
    const nom = champNom.value.trim()
    const motDePasse = champMdp.value
    if (nom.length === 0 || motDePasse.length === 0) {
      erreur.textContent = 'Entre un nom et un mot de passe.'
      return
    }

    valider.disabled = true
    erreur.textContent = ''
    journal.input('connexion', `tentative pour "${nom}"`)

    try {
      const r = await fetch('/api/connexion', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ nom, motDePasse }),
      })
      if (r.status === 401) {
        erreur.textContent = 'Nom ou mot de passe incorrect.'
        champMdp.value = ''
        champMdp.focus()
        journal.warn('connexion', `refuse pour "${nom}"`)
        return
      }
      if (r.status === 429) {
        // Le frein du serveur contre les essais en rafale: le dire tel quel.
        erreur.textContent = 'Trop d’essais. Réessaie dans quelques minutes.'
        journal.warn('connexion', `frein du serveur pour "${nom}"`)
        return
      }
      if (!r.ok) throw new Error(`HTTP ${r.status}`)
      const c = (await r.json()) as ReponseConnexion
      const admin = c.admin === true
      journal.info('connexion', `"${c.nom}" connecte`, { aUnPersonnage: c.aUnPersonnage, admin })
      this.terminer({ token: c.token, compte: c.nom, aUnPersonnage: c.aUnPersonnage, admin })
    } catch (e) {
      erreur.textContent = 'Le serveur ne répond pas. Réessaie dans un instant.'
      journal.error('connexion', `echec reseau: ${String(e)}`)
    } finally {
      valider.disabled = false
    }
  }

  private terminer(r: ResultatConnexion): void {
    // Memorise pour cette session: le HUD saura s'il doit montrer le bouton
    // d'administration, sans avoir a redemander au serveur.
    ecrireLocal(CLE_ADMIN, r.admin ? '1' : '0')
    const resoudre = this.resoudre
    this.resoudre = null
    this.detruire()
    resoudre?.(r)
  }

  detruire(): void {
    this.racine?.remove()
    this.racine = null
  }
}
